import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { getSectionVisibility } from '../config/sectionVisibility';
import { BRAND } from '../constants/brand';
import LoadingSpinner from './ui/LoadingSpinner';
import ParticleBackground from './ParticleBackground';
import FloatingActionMenu from './FloatingActionMenu';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, content, isLoading } = useLanguage();
  const sectionVisibility = getSectionVisibility();
  const [scrollProgress, setScrollProgress] = useState(0);

  // Track scroll progress for the progress indicator
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Generate navigation items based on current language and visibility settings
  const getNavItems = () => {
    const baseRoutes = language === 'en' ? '/en' : '';
    const routes = language === 'en' 
      ? ['', '/services', '/technologies', '/about', '/contact']
      : ['', '/uslugi', '/technologie', '/o-mnie', '/kontakt'];
    
    const allItems = [
      { path: baseRoutes + routes[0] || '/', label: content.navigation.home, visible: sectionVisibility.homeNavItem },
      { path: baseRoutes + routes[2], label: content.navigation.technologies, visible: sectionVisibility.technologiesNavItem },
      { path: baseRoutes + routes[1], label: content.navigation.services, visible: sectionVisibility.servicesNavItem },
      { path: baseRoutes + routes[3], label: content.navigation.about, visible: sectionVisibility.aboutNavItem },
      { path: baseRoutes + routes[4], label: content.navigation.contact, visible: sectionVisibility.contactNavItem }
    ];
    
    // Filter out hidden items
    return allItems.filter(item => item.visible);
  };

  const navItems = getNavItems();

  // Language switcher function
  const handleLanguageSwitch = () => {
    const currentPath = location.pathname;
    let newPath = '/';

    console.log('Current language:', language, 'Current path:', currentPath);

    if (language === 'pl') {
      // Switch to English
      if (currentPath === '/') newPath = '/en';
      else if (currentPath === '/uslugi') newPath = '/en/services';
      else if (currentPath === '/technologie') newPath = '/en/technologies';
      else if (currentPath === '/o-mnie') newPath = '/en/about';
      else if (currentPath === '/kontakt') newPath = '/en/contact';
      else newPath = '/en'; // fallback to English home
    } else {
      // Switch to Polish
      if (currentPath === '/en') newPath = '/';
      else if (currentPath === '/en/services') newPath = '/uslugi';
      else if (currentPath === '/en/technologies') newPath = '/technologie';
      else if (currentPath === '/en/about') newPath = '/o-mnie';
      else if (currentPath === '/en/contact') newPath = '/kontakt';
      else newPath = '/'; // fallback to Polish home
    }

    console.log('Navigating to:', newPath);
    navigate(newPath);
  };

  // Show loading spinner during language changes
  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-x-hidden">
      {/* Particle Background */}
      <ParticleBackground />
      
      {/* Floating Action Menu */}
      <FloatingActionMenu 
        onLanguageToggle={handleLanguageSwitch}
        currentLanguage={language}
      />
      
      {/* Navigation */}
      <nav className="sticky top-0 bg-white/5 backdrop-blur-md border-b border-gray-700/30 z-40 shadow-lg shadow-black/20">
        {/* Scroll Progress Indicator */}
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#aa96da] to-[#20b2aa]"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
          animate={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1, ease: "easeOut" }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-24">
            {/* Enhanced Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center group">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative"
                >
                  <img 
                    src="/logo-no-background.svg" 
                    alt={BRAND.COMPANY_NAME} 
                    className="h-10 md:h-14 w-auto transition-all duration-300 group-hover:drop-shadow-lg"
                    style={{
                      filter: 'drop-shadow(0 0 20px rgba(170, 150, 218, 0.3))'
                    }}
                  />
                </motion.div>
              </Link>
            </div>

            {/* Enhanced Navigation Links - Hidden on mobile */}
            <div className="hidden lg:flex items-center space-x-6">
              {navItems.filter(item => item.label !== content.navigation.contact).map((item) => (
                <motion.div
                  key={item.path}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={item.path}
                    className={`relative px-5 py-3 text-lg font-semibold tracking-wide transition-all duration-300 rounded-lg group ${
                      location.pathname === item.path
                        ? 'text-[#aa96da] bg-[#aa96da]/10'
                        : 'text-gray-300 hover:text-[#20b2aa] hover:bg-[#aa96da]/5'
                    }`}
                  >
                    <span className="relative z-10">{item.label}</span>
                    
                    {/* Active page indicator */}
                    {location.pathname === item.path && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#aa96da] to-[#20b2aa] rounded-full"
                        layoutId="navbar-indicator"
                        initial={false}
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                    
                    {/* Hover underline */}
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#20b2aa] rounded-full origin-left"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: location.pathname !== item.path ? 1 : 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                    
                    {/* Hover background */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-[#aa96da]/10 to-[#20b2aa]/10 rounded-lg"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: location.pathname !== item.path ? 1 : 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  </Link>
                </motion.div>
              ))}
              
              {/* Language Switcher */}
              {sectionVisibility.languageFloatingButton && (
                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="ml-8"
                >
                  <button
                    onClick={handleLanguageSwitch}
                    className="relative px-4 py-2 text-lg font-semibold tracking-wide text-gray-300 hover:text-[#20b2aa] rounded-lg border border-gray-600 hover:border-[#20b2aa] transition-all duration-300"
                  >
                    <span className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                      <span>{language === 'en' ? 'PL' : 'EN'}</span>
                    </span>
                  </button>
                </motion.div>
              )}

              {/* Enhanced Contact CTA Button */}
              {sectionVisibility.contactNavItem && (
                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="ml-6"
                >
                  <Link
                    to={navItems.find(item => item.label === content.navigation.contact)?.path || '/kontakt'}
                    className="relative px-6 py-3 text-lg font-bold tracking-wide text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl isolate"
                    style={{
                      background: 'linear-gradient(135deg, #aa96da, #20b2aa)',
                      boxShadow: '0 4px 15px rgba(170, 150, 218, 0.4)'
                    }}
                  >
                    <span className="relative z-10">{content.navigation.contact}</span>
                  </Link>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 overflow-x-hidden">
        {children}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout;
